import { call } from '@everlutionsk/helpers';
import { createUiTheme, UiTheme } from '@everlutionsk/ui';
import { createUiAdminTheme, UiAdminTheme } from '@everlutionsk/ui-admin';
import { createUiApolloTheme } from '@everlutionsk/ui-apollo';
import { UiApolloTheme } from '@everlutionsk/ui-apollo/dist/esm/src/theme';
import { createUiFormikTheme, UiFormikTheme } from '@everlutionsk/ui-formik';
import { alpha, createTheme as createMuiTheme, Slide, ThemeOptions } from '@mui/material';
import React, { useMemo } from 'react';
import { colorPrimitives, colorsDark, colorsLight, useConfig } from './config';
import { useModeContext } from './modeContext';

export const { brandPalette } = useConfig();

interface ThemeProps {
  readonly isProd: boolean;
  readonly mode: 'light' | 'dark';
  readonly accentColor?: string;
}

export function createTheme({ mode, isProd, accentColor }: ThemeProps) {
  const containerPadding = 'clamp(15px, 3vw, 50px)';

  const effectivePalette = call((): (typeof brandPalette)[typeof mode] => {
    const base = brandPalette[mode];
    if (accentColor == null) return base;
    return {
      ...base,
      foreground: { ...base.foreground, accent: accentColor },
      background: { ...base.background, accent: accentColor }
    };
  });
  const spacing = [0, 2, 4, 8, 12, 16, 20, 24, 32, 40, 48, 64, 80, 96, 120];

  const spacingBuilder = (factor: number | string): string => {
    if (typeof factor === 'string') return factor;

    const index = Math.floor(factor);
    const currentSpace = spacing[index];
    const nextSpace = spacing[index + 1] ?? currentSpace * 2;
    const space = currentSpace + (nextSpace - currentSpace) * (factor - index);
    return `${space}px`;
  };

  const radiusBuilder: ThemeOptions['radius'] = {
    none: '0px',
    xxs: '2px',
    xs: '4px',
    sm: '6px',
    md: '8px',
    l: '10px',
    xl: '12px',
    '2xl': '16px',
    '3xl': '20px',
    '4xl': '24px',
    '5xl': '40px',
    full: '999px',
    circle: '100%'
  };

  const shadowsBuilder = {
    none: '0px 0px 0px 0px rgba(0, 0, 0, 0)',
    xs: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    s: '0px 2px 4px 0px rgba(27, 28, 29, 0.05)',
    m: '0px 16px 32px -12px rgba(88, 92, 95, 0.10)',
    l: '0px 0px 0px 1px #E8EAEE, 0px 12px 40px -8px rgba(88, 92, 95, 0.06), 0px 12px 30px -13px rgba(88, 92, 95, 0.12)',
    xl: ' 0px 0px 0px 1px #E8EAEE, 0px 16px 40px -8px rgba(88, 92, 95, 0.16)',
    '2xl': ' 0px 24px 48px -12px rgba(16, 24, 40, 0.18)',
    '3xl': '0px 32px 64px -12px rgba(16, 24, 40, 0.15)',
    '4xl': ' 0 0 15px 0 rgba(0, 0, 0, 0.15)'
  };

  const focusRingsBuilder = {
    primary: '0px 0px 0px 4px #25FFC5',
    secondary: '0px 0px 0px 4px #03A6FF',
    tertiary: '0px 0px 0px 4px #E4FF34',
    error: '0px 0px 0px 4px #D92D1F',
    success: '0px 0px 0px 4px #17B26A'
  };

  const breakpointsBuilder: ThemeOptions['breakpoints'] = {
    values: {
      xs: 0,
      sm: 490,
      md: 768,
      lg: 1040,
      xl: 1440
    }
  };

  const typographyBuilder: ThemeOptions['typography'] = {
    fontFamily: [
      'Inter',
      'Consolas',
      'sans-serif',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"'
    ].join(','),
    h1: {
      fontFamily: 'InterDisplay',
      fontSize: '3.5rem',
      lineHeight: '4rem',
      letterSpacing: '-0.56px'
    },
    h2: {
      fontFamily: 'InterDisplay',
      fontSize: '3rem',
      lineHeight: '3.5rem',
      letterSpacing: '-0.48px'
    },
    h3: {
      fontFamily: 'InterDisplay',
      fontSize: '2.5rem',
      lineHeight: '3rem',
      letterSpacing: '-0.4px'
    },
    h4: {
      fontFamily: 'InterDisplay',
      fontSize: '2rem',
      lineHeight: '2.5rem'
    },
    h5: {
      fontFamily: 'InterDisplay',
      fontSize: '1.5rem',
      lineHeight: '1.75rem'
    },
    h6: {
      fontFamily: 'InterDisplay',
      fontSize: '1.25rem',
      lineHeight: '1.75rem'
    },
    labelXLarge: {
      fontSize: '1.5rem',
      lineHeight: '2rem',
      letterSpacing: '-0.24px'
    },
    labelLarge: {
      fontSize: '1.125rem',
      lineHeight: '1.5rem',
      letterSpacing: '-0.18px'
    },
    labelMedium: {
      fontSize: '1rem',
      lineHeight: '1.5rem',
      letterSpacing: '-0.16px'
    },
    labelSmall: {
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      letterSpacing: '-0.07px'
    },
    labelXSmall: {
      fontSize: '0.75rem',
      lineHeight: '1rem'
    },
    label2XSmall: {
      fontSize: '0.625rem',
      lineHeight: '0.875rem',
      letterSpacing: '0%'
    },
    subheadingMedium: {
      fontSize: '1rem',
      lineHeight: '1.5rem',
      letterSpacing: '0.96px'
    },
    subheadingSmall: {
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      letterSpacing: '0.84px'
    },
    subheadingXSmall: {
      fontSize: '0.75rem',
      lineHeight: '1rem',
      letterSpacing: '0.48px'
    },
    subheading2XSmall: {
      fontSize: '0.625rem',
      lineHeight: '0.875rem',
      letterSpacing: '0.2px'
    },
    underlineSmall: {
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      letterSpacing: '-0.5%',
      textDecoration: 'underline'
    },
    underlineXSmall: {
      fontSize: '0.75rem',
      lineHeight: '1rem',
      letterSpacing: '0%',
      textDecoration: 'underline'
    },
    dottedUnderlineSmall: {
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      letterSpacing: '-0.5%',
      textDecoration: 'underline dotted'
    },
    dottedUnderlineXSmall: {
      fontSize: '0.75rem',
      lineHeight: '1rem',
      letterSpacing: '0%',
      textDecoration: 'underline dotted'
    },
    codeSmallMedium: {
      fontFamily: 'Consolas',
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      letterSpacing: '-0.5%'
    }
  };

  const componentsBuilder: ThemeOptions['components'] = {
    MuiTypography: {
      styleOverrides: {
        root: {}
      }
    },
    MuiCssBaseline: {
      styleOverrides: `
        body {
          color: ${effectivePalette.foreground.neutral};
        }

        ::-webkit-scrollbar{
          background: transparent;
          width: 5px;
        }

        ::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 15px;
        }
      `
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: effectivePalette.background.neutral,
          color: effectivePalette.foreground.neutral,
          // padding: `${spacing[7]}px ${spacing[9]}px`,
          contain: 'content',
          border: 'none',
          boxShadow: shadowsBuilder.none,
          '& .MuiToolbar-root': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 0,
            minHeight: '40px !important'
          }
        }
      }
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: false
      },
      styleOverrides: {
        root: {
          minWidth: '100%',
          paddingLeft: `${containerPadding}`,
          paddingRight: `${containerPadding}`
        },
        disableGutters: {
          paddingLeft: '0 !important',
          paddingRight: '0 !important'
        },
        /* Removes horizontal centering of containers */
        maxWidthXl: {
          marginLeft: 0
        },
        maxWidthLg: {
          marginLeft: 0
        },
        maxWidthMd: {
          marginLeft: 0
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: 6
        }
      }
    },
    MuiTable: {
      defaultProps: {
        size: 'small'
      },
      styleOverrides: {
        root: {
          borderRadius: radiusBuilder.sm,
          borderCollapse: 'initial',
          overflow: 'clip',
          '& .MuiTableRow-root': {
            paddingRight: '24px'
          },

          '& .MuiTableCell-sizeSmall': {
            padding: '16px 24px'
          },
          '& .MuiTableCell-root': {
            borderBottom: `2px solid ${effectivePalette.background.neutral}`
          }
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: brandPalette[200]
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${effectivePalette.border.neutral}`
        }
      }
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            '&:last-of-type': {
              '& .MuiTableCell-root': {
                border: 'none'
              }
            },
            '&.MuiTableRow-hover': {
              '&:hover': {
                backgroundColor: alpha(effectivePalette.border.neutral, 0.25)
              }
            }
          }
        }
      }
    },
    MuiTabs: {
      defaultProps: {
        scrollButtons: true,
        allowScrollButtonsMobile: true
      },
      styleOverrides: {
        root: {
          overflow: 'visible'
        },
        flexContainer: {
          gap: spacing[4]
        },
        indicator: {
          height: '2px',
          backgroundColor: effectivePalette.foreground.accent,
          borderRadius: '2px 2px 0 0'
        }
      }
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          lineHeight: '1rem',
          fontSize: '0.75rem',
          fontWeight: 530,
          letterSpacing: '3%',
          color: effectivePalette.foreground.neutralFaded
        }
      }
    },
    MuiPaper: {
      defaultProps: {
        variant: 'elevation'
      }
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small'
      },
      styleOverrides: {
        root: {
          '& .MuiFormLabel-root': {
            fontSize: '0.875rem'
          },
          '& .MuiInputBase-root': {
            backgroundColor: '#fff'
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: effectivePalette.background.inverseFaded
          }
        }
      }
    },
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        TransitionComponent: props => <Slide {...props} direction="left" />
      }
    },
    MuiSlider: {
      defaultProps: {
        valueLabelDisplay: 'off'
      }
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
        color: 'primary'
      },
      styleOverrides: {
        // Makes sure `<Link component="button"/>` looks the same like `<Link />`
        button: {
          verticalAlign: undefined,
          fontSize: 'inherit'
        },
        root: {
          cursor: 'pointer',
          fontWeight: 500,
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline'
          }
        }
      }
    },
    MuiDialog: {
      defaultProps: {
        maxWidth: 'xs',
        fullWidth: true
      },
      styleOverrides: {
        paper: {
          // Fix scroll issues
          '& > form': {
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          width: '100%',
          borderRadius: radiusBuilder.md,
          border: '1px solid transparent',
          transition: 'border 0.2s ease-in-out, background 0.2s ease-in-out',
          fontSize: typographyBuilder.labelSmall.fontSize,
          lineHeight: typographyBuilder.labelSmall.lineHeight,

          padding: spacing[4] + 'px',
          color: effectivePalette.foreground.neutral,
          backgroundColor: effectivePalette.background.secondary,

          input: {
            padding: 0,
            height: '20px !important',
            width: '100%'
          },
          'input::placeholder': {
            color: effectivePalette.foreground.placeholder,
            opacity: '1 !important'
          },

          '&.Mui-disabled input::placeholder': {
            color: effectivePalette.foreground.disabled
          },

          '&:hover': {
            backgroundColor: effectivePalette.background.secondaryHover
          },

          '&:focus-within': {
            borderColor: effectivePalette.border.active,
            backgroundColor: effectivePalette.background.neutral
          },

          '&.Mui-disabled': {
            backgroundColor: effectivePalette.background.secondaryHover,
            cursor: 'disabled'
          },

          '&.Mui-error': {
            // border: '1.5px solid transparent',
            borderColor: effectivePalette.border.negativeFaded
          },

          // TODO check hover styles of autofill
          'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active':
            {
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: effectivePalette.foreground.neutral,
              transition: 'background-color 5000s ease-in-out 0s',
              WebkitBoxShadow: `${shadowsBuilder.none} !important`,
              lineHeight: '1.25rem',
              fontSize: '0.875rem',
              fontWeight: 400,
              letterSpacing: '0.42px'
            },
          '& input[type=number]': {
            fontSize: '0.875rem',
            MozAppearance: 'textfield',
            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0
            }
          },
          '& input[type=tel]': {
            fontSize: '0.875rem'
          }
        }
      }
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          background: effectivePalette.background.neutral,
          boxShadow: shadowsBuilder.l,
          borderRadius: radiusBuilder.md,

          '.MuiPickersCalendarHeader-labelContainer': {
            fontWeight: '400'
          }
        }
      }
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          color: effectivePalette.foreground.neutralFaded,
          width: '32px'
        }
      }
    },
    MuiPickersArrowSwitcher: {
      styleOverrides: {
        button: {
          color: effectivePalette.foreground.neutral
        }
      }
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        label: {
          color: effectivePalette.foreground.neutral,
          fontSize: '0.875rem',
          marginRight: '4px',
          fontWeight: '400'
        },
        switchViewButton: {
          color: effectivePalette.foreground.neutral
        }
      }
    },
    MuiPickersYear: {
      styleOverrides: {
        root: {
          color: effectivePalette.foreground.neutral,
          background: effectivePalette.background.neutral
        },
        yearButton: {
          color: effectivePalette.foreground.neutral,

          '&.Mui-selected': {
            background: effectivePalette.background.secondaryActive + '!important',
            color: effectivePalette.foreground.inverse
          }
        }
      }
    },
    MuiPickersMonth: {
      styleOverrides: {
        root: {
          color: effectivePalette.foreground.neutral,

          '&.Mui-selected': {
            background: effectivePalette.background.secondaryActive + '!important',
            color: effectivePalette.foreground.inverse
          }
        }
      }
    },
    MuiPickersDay: {
      dayDisabled: {
        color: '#ccc'
      },
      styleOverrides: {
        root: {
          color: effectivePalette.foreground.neutral,
          width: '32px',
          height: '32px',

          '&.Mui-selected': {
            background: effectivePalette.background.secondaryActive + '!important',
            color: effectivePalette.foreground.inverse
          }
        },
        today: {
          border: `1.5px solid ${effectivePalette.border.neutral} !important`
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radiusBuilder.xs + 'px',
          color: effectivePalette.foreground.inverse,
          padding: spacing[4],
          display: 'inline-flex',
          justifyContent: 'space-between',

          minHeight: '40px',
          minWidth: '200px'
        },
        label: {
          padding: 0
        },
        deleteIcon: {
          margin: 0,
          width: '16px',
          height: '16px',
          marginLeft: spacing[2] + 'px',
          color: effectivePalette.foreground.inverse
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        // @ts-ignore https://github.com/mui/material-ui/issues/25763
        root: {
          padding: 0,
          display: 'block',
          borderRadius: radiusBuilder.md,
          width: '100%',

          '.MuiOutlinedInput-notchedOutline': {
            borderWidth: '0px !important'
          },

          '.MuiSelect-icon': {
            color: effectivePalette.foreground.inputIcon
          },
          '.MuiList-root': {
            marginTop: '8px',
            padding: '8px',
            borderRadius: radiusBuilder.md
          }
        },
        select: {
          maxWidth: '100%',
          width: 'auto !important',
          minHeight: 'auto !important',
          height: '20px',
          paddingTop: spacing[4] + 'px',
          paddingBottom: spacing[4] + 'px'
        }
      }
    },
    MuiList: {
      styleOverrides: {
        padding: {
          padding: 0
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: `${spacing[4]}px`,
          color: effectivePalette.foreground.neutral,
          fontSize: '0.875rem',

          transition: 'background-color 0.2s ease-in-out',

          '&.Mui-selected, &.Mui-selected:hover': {
            background: effectivePalette.background.secondaryHover
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '& .MuiTypography-root': {
            marginBottom: spacing[2]
          }
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          color: effectivePalette.foreground.neutralFaded,
          backgroundColor: effectivePalette.background.faded,
          border: `1px solid ${effectivePalette.border.neutral}`
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: '0.9rem'
        }
      }
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontSize: '1rem'
        }
      }
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          width: '1.4em',
          height: '1.4em',
          '&.Mui-active': {
            color: effectivePalette.foreground.accent
          }
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.75rem',
          paddingLeft: '1.5rem',
          paddingRight: '1.5rem'
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          paddingTop: '6px',
          paddingLeft: '1.5rem',
          paddingRight: '1.5rem'
        }
      }
    },
    MuiDialogContentText: {
      defaultProps: {
        marginBottom: 1
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          justifyContent: 'flex-start',
          paddingLeft: '1.5rem',
          paddingRight: '1.5rem',
          paddingBottom: '1.5rem'
        }
      }
    },
    MuiCheckbox: {
      defaultProps: {
        color: 'primary'
      }
    },
    MuiSwitch: {
      defaultProps: {
        color: 'success'
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          maxWidth: '600px'
        }
      }
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          marginLeft: '.4rem',
          marginRight: '.4rem'
        },
        root: {
          '& .MuiSvgIcon-root': {
            fontSize: '1rem',
            marginRight: '0.5rem'
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `solid 1px ${effectivePalette.border.neutral}`,
          borderRadius: radiusBuilder.sm,
          backgroundColor: '#FFFFFF'
        }
      }
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .Mui-selected': {
            backgroundColor: `${effectivePalette.data.dimAccent}!important`,
            color: effectivePalette.foreground.inverse
          }
        }
      }
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          color: effectivePalette.foreground.neutralFaded
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        icon: {
          marginLeft: '6px',
          alignItems: 'center'
        },
        root: {
          border: 0,
          borderRadius: radiusBuilder.sm,
          alignItems: 'center'
        },
        message: {
          padding: '2px 0'
        },
        action: {
          paddingTop: 0
        }
      }
    },
    MuiIconButton: {
      defaultProps: {
        color: 'primary'
      },
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: effectivePalette.background.neutralHover
          },
          '&:focus': {
            background: 'transparent'
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none'
        },
        startIcon: {
          marginLeft: 0
        }
      }
    },
    MuiCollapse: {
      styleOverrides: {
        root: {
          contain: 'content',
          transition: 'height 180ms cubic-bezier(.39,.34,.35,1) 0ms !important'
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: effectivePalette.border.neutralFaded
        }
      }
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: effectivePalette.border.neutralFaded
        }
      }
    }
  } as any;

  const paletteBuilder: ThemeOptions['palette'] = {
    mode,
    neutral: {
      main: effectivePalette.foreground.neutral
    },
    error: {
      main: effectivePalette.foreground.negativeOffset
    },
    success: {
      main: effectivePalette.foreground.success
    },
    warning: {
      main: effectivePalette.foreground.warning
    },
    primary: {
      main: effectivePalette.foreground.accent
    },
    background: {
      default: effectivePalette.background.neutral,
      paper: effectivePalette.background.faded
    },
    grey: colorPrimitives.gray,
    text: {
      primary: effectivePalette.foreground.neutralOnAccent,
      secondary: effectivePalette.foreground.neutral,
      disabled: effectivePalette.foreground.disabled
    },
    action: {
      active: effectivePalette.foreground.neutralOnAccent,
      disabledBackground: effectivePalette.background.disabled,
      disabled: effectivePalette.foreground.disabled
    }
  };

  const customTheme = createMuiTheme({
    palette: paletteBuilder,
    spacing: spacingBuilder,
    radius: radiusBuilder,
    effects: { shadows: shadowsBuilder, focusRings: focusRingsBuilder },
    breakpoints: breakpointsBuilder,
    brandPalette: effectivePalette,
    typography: typographyBuilder,
    components: componentsBuilder
  });

  const uiTheme = createUiTheme(customTheme, {
    SearchField: {
      defaultProps: {
        fullWidth: true
      }
    },
    Tabs: {
      indicatorColor: '#FFF',
      activeTextColor: brandPalette[900],
      inactiveTextColor: '#aaa'
    },
    Table: {
      defaultProps: {
        paginationPosition: 'both'
      }
    }
  });

  const uiAdminTheme = createUiAdminTheme(customTheme, {
    CardContent: {
      layerStyle: {
        border: '0',
        boxShadow: shadowsBuilder.none,
        borderRadius: radiusBuilder.none,
        backgroundColor: '#FFFFFF',
        overflow: 'unset'
      }
    }
  });

  const uiApolloTheme = createUiApolloTheme(customTheme, {
    pagination: {
      rowsPerPage: 20,
      rowsPerPageOptions: [10, 20, 50]
    }
  });

  const uiFormikTheme = createUiFormikTheme(customTheme, {
    Fields: {
      fieldStyle: {
        marginBottom: '1.2rem'
      }
    },
    SubmitButton: {
      defaultProps: {
        variant: 'contained'
      }
    }
  });

  const theme = {
    ...customTheme,
    ...uiTheme,
    ...uiAdminTheme,
    ...uiApolloTheme,
    ...uiFormikTheme
  };

  return theme;
}

export function useTheme({
  isProd,
  mode = 'light',
  accentColor
}: {
  isProd: boolean;
  mode?: 'light' | 'dark';
  accentColor?: string;
}) {
  return useMemo(() => {
    const theme = createTheme({
      isProd,
      mode,
      accentColor
    });

    return theme;
  }, [mode, isProd, accentColor]);
}

declare module '@mui/material/styles' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface Theme extends UiTheme, UiAdminTheme, UiApolloTheme, UiFormikTheme {}

  interface Theme {
    radius: {
      none: string;
      xxs: string;
      xs: string;
      sm: string;
      md: string;
      l: string;
      xl: string;
      '2xl': string;
      '3xl': string;
      '4xl': string;
      '5xl': string;
      full: string;
      circle: string;
    };
    effects: {
      shadows: {
        none: string;
        xs: string;
        s: string;
        m: string;
        l: string;
        xl: string;
        '2xl': string;
        '3xl': string;
      };
      focusRings: {
        primary: string;
        secondary: string;
        tertiary: string;
        error: string;
        success: string;
      };
    };
    brandPalette: typeof colorsDark | typeof colorsLight;
  }

  interface ThemeOptions {
    radius?: {
      none?: string;
      xxs?: string;
      xs?: string;
      sm?: string;
      md?: string;
      l?: string;
      xl?: string;
      '2xl'?: string;
      '3xl'?: string;
      '4xl'?: string;
      '5xl'?: string;
      full?: string;
      circle?: string;
    };

    effects: {
      shadows: {
        none: string;
        xs: string;
        s: string;
        m: string;
        l: string;
        xl: string;
        '2xl': string;
        '3xl': string;
      };
      focusRings: {
        primary: string;
        secondary: string;
        tertiary: string;
        error: string;
        success: string;
      };
    };
    brandPalette: typeof colorsDark | typeof colorsLight;
  }

  interface Palette {
    neutral: Palette['primary'];
  }

  interface PaletteOptions {
    neutral: PaletteOptions['primary'];
  }

  interface TypographyVariants {
    labelXLarge: React.CSSProperties;
    labelLarge: React.CSSProperties;
    labelMedium: React.CSSProperties;
    labelSmall: React.CSSProperties;
    labelXSmall: React.CSSProperties;
    label2XSmall: React.CSSProperties;

    subheadingMedium: React.CSSProperties;
    subheadingSmall: React.CSSProperties;
    subheadingXSmall: React.CSSProperties;
    subheading2XSmall: React.CSSProperties;

    underlineXSmall: React.CSSProperties;
    underlineSmall: React.CSSProperties;

    dottedUnderlineSmall: React.CSSProperties;
    dottedUnderlineXSmall: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    labelXLarge: React.CSSProperties;
    labelLarge: React.CSSProperties;
    labelMedium: React.CSSProperties;
    labelSmall: React.CSSProperties;
    labelXSmall: React.CSSProperties;
    label2XSmall: React.CSSProperties;

    subheadingMedium: React.CSSProperties;
    subheadingSmall: React.CSSProperties;
    subheadingXSmall: React.CSSProperties;
    subheading2XSmall: React.CSSProperties;

    underlineXSmall: React.CSSProperties;
    underlineSmall: React.CSSProperties;

    dottedUnderlineSmall: React.CSSProperties;
    dottedUnderlineXSmall: React.CSSProperties;

    codeSmallMedium: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    labelXLarge: true;
    labelLarge: true;
    labelMedium: true;
    labelSmall: true;
    labelXSmall: true;
    label2XSmall: true;

    subheadingMedium: true;
    subheadingSmall: true;
    subheadingXSmall: true;
    subheading2XSmall: true;

    underlineXSmall: true;
    underlineSmall: true;

    dottedUnderlineSmall: true;
    dottedUnderlineXSmall: true;

    codeSmallMedium: true;
  }
}

export function isDarkMode(): boolean {
  const [{ mode }] = useModeContext();
  return mode === 'dark';
}

export function isLightMode(): boolean {
  const [{ mode }] = useModeContext();
  return mode === 'light';
}
